import React, { useEffect, useState } from "react";
import {
  createAppUpdate,
  updateAppUpdate
} from "./adminAppUpdateService";
import "./appUpdate.css";

const AppUpdateForm = ({ appUpdate, onSaved, onCancel }) => {
  const [formData, setFormData] = useState({
    platform: "ANDROID",
    packageName: "",
    latestVersionCode: 1,
    latestVersionName: "",
    minSupportedVersionCode: 1,
    updateTitle: "",
    updateMessage: "",
    storeUrl: "",
    active: true
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appUpdate) {
      setFormData({
        platform: appUpdate.platform || "ANDROID",
        packageName: appUpdate.packageName || "",
        latestVersionCode: appUpdate.latestVersionCode || 1,
        latestVersionName: appUpdate.latestVersionName || "",
        minSupportedVersionCode: appUpdate.minSupportedVersionCode || 1,
        updateTitle: appUpdate.updateTitle || "",
        updateMessage: appUpdate.updateMessage || "",
        storeUrl: appUpdate.storeUrl || "",
        active: appUpdate.active ?? true
      });
    }
  }, [appUpdate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "latestVersionCode" ||
            name === "minSupportedVersionCode"
          ? Number(value)
          : value
    }));
  };

  const validateForm = () => {
    if (!formData.platform.trim()) {
      alert("Platform is required");
      return false;
    }

    if (!formData.packageName.trim()) {
      alert("Package name is required");
      return false;
    }

    if (!formData.latestVersionCode || formData.latestVersionCode <= 0) {
      alert("Latest version code must be greater than 0");
      return false;
    }

    if (!formData.latestVersionName.trim()) {
      alert("Latest version name is required");
      return false;
    }

    if (
      !formData.minSupportedVersionCode ||
      formData.minSupportedVersionCode <= 0
    ) {
      alert("Minimum supported version code must be greater than 0");
      return false;
    }

    if (
      formData.minSupportedVersionCode > formData.latestVersionCode
    ) {
      alert("Minimum supported version code cannot be greater than latest version code");
      return false;
    }

    if (!formData.updateTitle.trim()) {
      alert("Update title is required");
      return false;
    }

    if (!formData.updateMessage.trim()) {
      alert("Update message is required");
      return false;
    }

    if (!formData.storeUrl.trim()) {
      alert("Store URL is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);

    const payload = {
      platform: formData.platform.trim(),
      packageName: formData.packageName.trim(),
      latestVersionCode: Number(formData.latestVersionCode),
      latestVersionName: formData.latestVersionName.trim(),
      minSupportedVersionCode: Number(formData.minSupportedVersionCode),
      updateTitle: formData.updateTitle.trim(),
      updateMessage: formData.updateMessage.trim(),
      storeUrl: formData.storeUrl.trim(),
      active: formData.active
    };

    let response;

    if (appUpdate?.id) {
      response = await updateAppUpdate(appUpdate.id, payload);
    } else {
      response = await createAppUpdate(payload);
    }

    setSaving(false);

    if (response.status === 200 || response.status === 201) {
      alert(response.message || "Saved successfully");
      onSaved();
    } else {
      alert(response.message || "Save failed");
    }
  };

  return (
    <div className="form-card">
      <div className="form-header">
        <h2>
          {appUpdate ? "Edit App Update" : "Create App Update"}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Platform</label>
            <select
              name="platform"
              value={formData.platform}
              onChange={handleChange}
            >
              <option value="ANDROID">ANDROID</option>
              <option value="IOS">IOS</option>
            </select>
          </div>

          <div className="form-group">
            <label>Package Name</label>
            <input
              type="text"
              name="packageName"
              value={formData.packageName}
              onChange={handleChange}
              placeholder="com.example.app"
            />
          </div>

          <div className="form-group">
            <label>Latest Version Code</label>
            <input
              type="number"
              name="latestVersionCode"
              value={formData.latestVersionCode}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div className="form-group">
            <label>Latest Version Name</label>
            <input
              type="text"
              name="latestVersionName"
              value={formData.latestVersionName}
              onChange={handleChange}
              placeholder="1.0.1"
            />
          </div>

          <div className="form-group">
            <label>Min Supported Version Code</label>
            <input
              type="number"
              name="minSupportedVersionCode"
              value={formData.minSupportedVersionCode}
              onChange={handleChange}
              min="1"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              &nbsp; Active
            </label>
          </div>

          <div className="form-group full-width">
            <label>Update Title</label>
            <input
              type="text"
              name="updateTitle"
              value={formData.updateTitle}
              onChange={handleChange}
              placeholder="Update Available"
            />
          </div>

          <div className="form-group full-width">
            <label>Update Message</label>
            <textarea
              name="updateMessage"
              value={formData.updateMessage}
              onChange={handleChange}
              rows="4"
              placeholder="A new version of the app is available. Please update to continue."
            />
          </div>

          <div className="form-group full-width">
            <label>Store URL</label>
            <input
              type="text"
              name="storeUrl"
              value={formData.storeUrl}
              onChange={handleChange}
              placeholder="https://play.google.com/store/apps/details?id=com.example.app"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn primary"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>

          <button
            type="button"
            className="btn secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppUpdateForm;